import type { AppContext, ViewCleanup } from "../app";
import {
  loadCountries,
  groupByRegion,
  acceptedNamesFor,
  flagUrlFor,
  REGION_ORDER,
  type CountryMetadata,
} from "../data/countries";

// Browse mode — a non-game tool for verifying that countries are labelled
// and categorized correctly. Pick a region, then step through each country
// with prev/next. The current country is named in the panel and highlighted
// on the globe; no typing, no scoring, no stats touched.

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export const mountBrowse = async (
  root: HTMLElement,
  ctx: AppContext,
): Promise<ViewCleanup> => {
  root.classList.add("view-browse");
  ctx.globe.setTargetIso(null);
  ctx.globe.setGuessIso(null);
  ctx.globe.setHeatmap(null);
  ctx.globe.setClickHandler(null);
  ctx.globe.setHoverEnabled(true);

  const table = await loadCountries();
  const grouped = groupByRegion(table);

  const populatedRegions = REGION_ORDER.filter(
    (r) => (grouped[r] ?? []).length > 0,
  );

  // Initial region — comes via the route dataset, or defaults to the first
  // populated region.
  const initialRegion =
    root.dataset.region && populatedRegions.includes(root.dataset.region)
      ? root.dataset.region
      : (populatedRegions[0] ?? "");

  const card = document.createElement("section");
  card.className = "browse-card";
  card.innerHTML = `
    <header class="browse-header">
      <button type="button" class="browse-back" aria-label="Back">← Back</button>
      <h1>Browse</h1>
    </header>
    <p class="browse-subtitle">Step through each country in a region. No scoring — for verifying labels and classification.</p>
    <label class="browse-region-row">
      <span class="browse-region-label">Region</span>
      <select class="browse-region-select"></select>
    </label>
    <div class="browse-counter" aria-live="polite"></div>
    <div class="browse-detail" aria-live="polite"></div>
    <div class="browse-controls">
      <button type="button" class="browse-prev">← Prev</button>
      <button type="button" class="browse-next">Next →</button>
    </div>
    <p class="browse-hint">Arrow keys also navigate.</p>
  `;
  root.appendChild(card);

  const back = card.querySelector(".browse-back") as HTMLButtonElement;
  const select = card.querySelector(".browse-region-select") as HTMLSelectElement;
  const counter = card.querySelector(".browse-counter") as HTMLDivElement;
  const detail = card.querySelector(".browse-detail") as HTMLDivElement;
  const prevBtn = card.querySelector(".browse-prev") as HTMLButtonElement;
  const nextBtn = card.querySelector(".browse-next") as HTMLButtonElement;

  for (const region of populatedRegions) {
    const opt = document.createElement("option");
    opt.value = region;
    opt.textContent = `${region} (${grouped[region]!.length})`;
    select.appendChild(opt);
  }
  select.value = initialRegion;

  let currentRegion = initialRegion;
  let currentList: CountryMetadata[] = grouped[currentRegion] ?? [];
  let index = 0;

  const renderCurrent = (): void => {
    if (currentList.length === 0) {
      counter.textContent = "";
      detail.innerHTML = `<p class="browse-empty">No countries in this region.</p>`;
      ctx.globe.setTargetIso(null);
      prevBtn.disabled = true;
      nextBtn.disabled = true;
      return;
    }
    const country = currentList[index]!;
    counter.textContent = `${index + 1} / ${currentList.length}`;
    const aliases = acceptedNamesFor(country).filter(
      (n) => n !== country.canonicalName,
    );
    const flagUrl = flagUrlFor(country);
    const flagHtml = flagUrl
      ? `<img class="browse-flag" src="${flagUrl}" alt="Flag of ${escapeHtml(country.canonicalName)}" />`
      : "";
    const factBlock = country.fact
      ? `
        <div class="browse-fact">
          ${country.fact.capital ? `<div class="browse-fact-capital">Capital: <strong>${escapeHtml(country.fact.capital)}</strong></div>` : ""}
          <p class="browse-fact-text">${escapeHtml(country.fact.fact)}</p>
          <p class="browse-fact-flag-desc">${escapeHtml(country.fact.flagDescription)}</p>
        </div>
      `
      : "";
    detail.innerHTML = `
      <div class="browse-name-row">
        ${flagHtml}
        <h2 class="browse-name">${escapeHtml(country.canonicalName)}</h2>
      </div>
      ${factBlock}
      <dl class="browse-fields">
        <dt>ISO</dt><dd>${escapeHtml(country.iso)}${country.iso2 ? ` / ${escapeHtml(country.iso2.toUpperCase())}` : ""}</dd>
        <dt>Region</dt><dd>${escapeHtml(country.region)}</dd>
        <dt>Continent</dt><dd>${escapeHtml(country.continent ?? "—")}</dd>
        <dt>Subregion</dt><dd>${escapeHtml(country.subregion ?? "—")}</dd>
        <dt>Centroid</dt><dd>${country.label.lat?.toFixed(2) ?? "?"}, ${country.label.lng?.toFixed(2) ?? "?"}</dd>
        <dt>Accepted names</dt><dd>${aliases.length > 0 ? escapeHtml(aliases.join(", ")) : "<em>(canonical only)</em>"}</dd>
      </dl>
    `;
    prevBtn.disabled = false;
    nextBtn.disabled = false;
    ctx.globe.setTargetIso(country.iso);
    void ctx.globe.flyToIso(country.iso, { durationMs: 600 });
  };

  const setRegion = (region: string): void => {
    currentRegion = region;
    currentList = grouped[region] ?? [];
    index = 0;
    renderCurrent();
  };

  const step = (delta: number): void => {
    if (currentList.length === 0) return;
    index =
      (index + delta + currentList.length) % currentList.length;
    renderCurrent();
  };

  const onRegionChange = (): void => setRegion(select.value);
  const onPrev = (): void => step(-1);
  const onNext = (): void => step(1);
  const onBack = (): void => ctx.nav.goRegionPicker();
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      step(1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      step(-1);
    }
  };

  select.addEventListener("change", onRegionChange);
  prevBtn.addEventListener("click", onPrev);
  nextBtn.addEventListener("click", onNext);
  back.addEventListener("click", onBack);
  window.addEventListener("keydown", onKey);

  setRegion(currentRegion);

  return () => {
    select.removeEventListener("change", onRegionChange);
    prevBtn.removeEventListener("click", onPrev);
    nextBtn.removeEventListener("click", onNext);
    back.removeEventListener("click", onBack);
    window.removeEventListener("keydown", onKey);
    ctx.globe.setTargetIso(null);
    root.classList.remove("view-browse");
  };
};
